import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  CampaignActionBody,
  CampaignAggregate,
  CampaignAnalysisRequest,
  CampaignAnalysisRow,
  CampaignResumeBody,
  DashboardResponse,
  DialerUsageRequest,
  DialerUsageRow,
  PilotCliRow,
  ResellerCampaignRow,
  ResellerUserRow,
  RunningCampaignRow,
  TotalCallsRequest,
  TotalCallsResponse,
  UserWiseSummaryRequest,
  UserWiseSummaryRow,
} from './types';

export const dashboardKeys = {
  all: ['dashboard'] as const,
  main: (userId: string | number | null) =>
    ['dashboard', 'main', userId] as const,
  superAggregate: (userId: string | number | null) =>
    ['dashboard', 'super-agg', userId] as const,
  resellerCampaigns: (userId: string | number | null) =>
    ['dashboard', 'reseller-campaigns', userId] as const,
  resellerUsers: (userId: string | number | null) =>
    ['dashboard', 'reseller-users', userId] as const,
  pilotCli: (cli: string, userId: string | number | null) =>
    ['dashboard', 'pilot-cli', cli, userId] as const,
  totalCalls: (userId: string | number | null, start: string, end: string) =>
    ['dashboard', 'total-calls', userId, start, end] as const,
  dialerUsage: (userId: string | number | null, start: string, end: string) =>
    ['dashboard', 'dialer-usage', userId, start, end] as const,
  campaignAnalysis: (req: CampaignAnalysisRequest) =>
    ['dashboard', 'campaign-analysis', req] as const,
  userwise: (req: UserWiseSummaryRequest) =>
    ['dashboard', 'userwise', req] as const,
  runningForUser: (id: string | number) =>
    ['dashboard', 'running-for-user', id] as const,
};

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: dashboardKeys.all });
}

/** GET /api/obd/dashboard/{userId} — main payload (header counters, charts,
 *  running campaigns, expenditure, weekly campaigns). */
export function useDashboardMain() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: dashboardKeys.main(userId),
    queryFn: () =>
      api.get<DashboardResponse>(`/api/obd/dashboard/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
  });
}

/** GET /api/obd/superadmin/campaign/list/{userId} — aggregated counters
 *  for the SUPER_ADMIN pills row. */
export function useSuperAdminCampaigns() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: dashboardKeys.superAggregate(userId),
    queryFn: () =>
      api.get<CampaignAggregate[]>(
        `/api/obd/superadmin/campaign/list/${userId}`,
      ),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (d) => ensureArray<CampaignAggregate>(d),
  });
}

/** GET /api/obd/reseller/campaign/list/{userId} — ADMIN running campaigns. */
export function useResellerCampaigns() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: dashboardKeys.resellerCampaigns(userId),
    queryFn: () =>
      api.get<ResellerCampaignRow[]>(
        `/api/obd/reseller/campaign/list/${userId}`,
      ),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (d) => ensureArray<ResellerCampaignRow>(d),
  });
}

/** GET /api/obd/reseller/users/{userId} — user picker for ADMIN analysis. */
export function useResellerUsers() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: dashboardKeys.resellerUsers(userId),
    queryFn: () =>
      api.get<ResellerUserRow[]>(`/api/obd/reseller/users/${userId}`),
    enabled: Boolean(userId),
    staleTime: 60_000,
    select: (d) => ensureArray<ResellerUserRow>(d),
  });
}

/** GET /api/obd/cli/report/pilot/{cli|''} — pilot-wise CLI report, scoped
 *  to the caller's own hierarchy (sub-admins only see their descendant
 *  users' CLIs) via the userId query param. */
export function usePilotCliReport(cli: string = '') {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: dashboardKeys.pilotCli(cli, userId),
    queryFn: () =>
      api.get<PilotCliRow[]>(
        `/api/obd/cli/report/pilot/${cli || ''}?userId=${userId ?? ''}`,
      ),
    enabled: Boolean(userId),
    staleTime: 60_000,
    select: (d) => ensureArray<PilotCliRow>(d),
  });
}

/** POST /api/obd/dashboard/totalCalls — date-range call totals. */
export function useTotalCalls(startDate: string, endDate: string) {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: dashboardKeys.totalCalls(userId, startDate, endDate),
    queryFn: () =>
      api.post<TotalCallsResponse>('/api/obd/dashboard/totalCalls', {
        userId: Number(userId),
        startDate,
        endDate,
      } as TotalCallsRequest),
    enabled: Boolean(userId && startDate && endDate),
    staleTime: 30_000,
  });
}

/** POST /api/obd/dialerwise/totalUsage — per-dialer + per-CLI durations. */
export function useDialerUsage(startDate: string, endDate: string) {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: dashboardKeys.dialerUsage(userId, startDate, endDate),
    queryFn: () =>
      api.post<DialerUsageRow[]>('/api/obd/dialerwise/totalUsage', {
        userId: Number(userId),
        startDate,
        endDate,
      } as DialerUsageRequest),
    enabled: Boolean(userId && startDate && endDate),
    staleTime: 30_000,
    select: (d) => ensureArray<DialerUsageRow>(d),
  });
}

/** POST /api/obd/campaign/analysis — analysis table + pie data. */
export function useCampaignAnalysis(req: CampaignAnalysisRequest | null) {
  return useQuery({
    queryKey: req
      ? dashboardKeys.campaignAnalysis(req)
      : ['dashboard', 'campaign-analysis', 'idle'],
    queryFn: () =>
      api.post<CampaignAnalysisRow[]>('/api/obd/campaign/analysis', req!),
    enabled: Boolean(req),
    staleTime: 30_000,
    select: (d) => ensureArray<CampaignAnalysisRow>(d),
  });
}

/** POST /api/obd/userwise/voice/campaigns/summary — userwise table, scoped
 *  to the caller's own hierarchy (sub-admins only see their descendant
 *  users' rows). userId is sourced from the auth store and merged in here
 *  so every call site gets the scoping for free. */
export function useUserwiseSummary(req: UserWiseSummaryRequest | null) {
  const userId = useAuthStore((s) => s.userId);
  const scopedReq = req ? { ...req, userId: userId ?? undefined } : null;
  return useQuery({
    queryKey: scopedReq
      ? dashboardKeys.userwise(scopedReq)
      : ['dashboard', 'userwise', 'idle'],
    queryFn: () =>
      api.post<UserWiseSummaryRow[]>(
        '/api/obd/userwise/voice/campaigns/summary',
        scopedReq!,
      ),
    enabled: Boolean(scopedReq),
    staleTime: 30_000,
    select: (d) => ensureArray<UserWiseSummaryRow>(d),
  });
}

/** GET /api/obd/campaign/sadmin/{userId} — running campaigns for a user
 *  the super-admin drills into. */
export function useCampaignsForUser(id: string | number | null) {
  return useQuery({
    queryKey: dashboardKeys.runningForUser(id ?? 'none'),
    queryFn: () =>
      api.get<RunningCampaignRow[]>(`/api/obd/campaign/sadmin/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
    select: (d) => ensureArray<RunningCampaignRow>(d),
  });
}

/* ---------------- Campaign actions ---------------- */

export function usePauseCampaign() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: CampaignActionBody) =>
      api.post<{ message?: string }>('/api/obd/campaign/pause', body),
    onSuccess: invalidate,
  });
}

export function useResumeCampaign() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: CampaignResumeBody) =>
      api.post<{ message?: string }>('/api/obd/campaign/resume', body),
    onSuccess: invalidate,
  });
}

export function useStopCampaign() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: CampaignActionBody) =>
      api.post<{ message?: string }>('/api/obd/campaign/stop', body),
    onSuccess: invalidate,
  });
}

/** POST /api/obd/edit/channels/{campId}/{channels} — channel-edit action. */
export function useEditChannels() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      campaignId,
      channels,
    }: {
      campaignId: string;
      channels: number;
    }) =>
      api.post<{ message?: string }>(
        `/api/obd/edit/channels/${campaignId}/${channels}`,
        {},
      ),
    onSuccess: invalidate,
  });
}

/**
 * POST /api/obd/user/statistic/summary — high-level rollup (totals across
 * a date range, scoped by userId). Used by the dashboard "Statistics"
 * card in the legacy app.
 */
export interface UserStatisticSummaryRequest {
  userId: number | string;
  startDate: string;
  endDate: string;
}

export function useUserStatisticSummary() {
  return useMutation({
    mutationFn: (body: UserStatisticSummaryRequest) =>
      api.post<Record<string, number | string>>(
        '/api/obd/user/statistic/summary',
        body,
      ),
  });
}
